/**
 * Plugin Page Registry
 *
 * Frontend registry for plugin-provided pages.
 * Works in conjunction with backend PluginPageRegistryService.
 * Part of Phase 0: Core Preparation for Modularization.
 */

import { create } from 'zustand';

/**
 * Page definition from plugin
 */
export interface PluginPageDefinition {
  /** Unique page ID */
  id: string;
  /** Plugin that owns this page */
  pluginId: string;
  /** Route path */
  path: string;
  /** Page title */
  title: string;
  /** Page icon */
  icon?: string;
  /** Component to render (lazy loaded or wrapped) */
  component: React.LazyExoticComponent<React.ComponentType<Record<string, unknown>>> | React.ComponentType<Record<string, unknown>> | null;
  /** Component URL for iframe */
  componentUrl?: string;
  /** Whether to show in navigation */
  showInNav?: boolean;
  /** Navigation order */
  order?: number;
  /** Breadcrumb configuration */
  breadcrumb?: {
    label: string;
    parent?: string;
  };
  /** Required permissions to view this page route */
  requiredPermissions?: string[];
}

/**
 * Page registry state
 */
interface PageRegistryState {
  /** Map of page ID to definition */
  pages: Map<string, PluginPageDefinition>;
  /** Map of path to page ID */
  pathToPage: Map<string, string>;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: string | null;
  
  // Actions
  registerPage: (page: PluginPageDefinition) => void;
  unregisterPage: (pageId: string) => void;
  unregisterPluginPages: (pluginId: string) => void;
  getPage: (pageId: string) => PluginPageDefinition | undefined;
  getPageByPath: (path: string) => PluginPageDefinition | undefined;
  getAllPages: () => PluginPageDefinition[];
  getNavigablePages: () => PluginPageDefinition[];
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  syncFromBackend: (pages: PluginPageDefinition[]) => void;
}

/**
 * Page registry store
 */
export const usePageRegistry = create<PageRegistryState>((set, get) => ({
  pages: new Map(),
  pathToPage: new Map(),
  isLoading: false,
  error: null,

  registerPage: (page: PluginPageDefinition) => {
    set((state) => {
      const newPages = new Map(state.pages);
      const newPathToPage = new Map(state.pathToPage);
      
      // Remove old path mapping if updating
      const existing = newPages.get(page.id);
      if (existing) {
        newPathToPage.delete(existing.path);
      }
      
      newPages.set(page.id, page);
      newPathToPage.set(page.path, page.id);
      
      return { pages: newPages, pathToPage: newPathToPage };
    });
  },

  unregisterPage: (pageId: string) => {
    set((state) => {
      const page = state.pages.get(pageId);
      if (!page) return state;
      
      const newPages = new Map(state.pages);
      const newPathToPage = new Map(state.pathToPage);
      
      newPages.delete(pageId);
      newPathToPage.delete(page.path);
      
      return { pages: newPages, pathToPage: newPathToPage };
    });
  },

  unregisterPluginPages: (pluginId: string) => {
    set((state) => {
      const newPages = new Map(state.pages);
      const newPathToPage = new Map(state.pathToPage);
      
      for (const [id, page] of newPages) {
        if (page.pluginId === pluginId) {
          newPages.delete(id);
          newPathToPage.delete(page.path);
        }
      }
      
      return { pages: newPages, pathToPage: newPathToPage };
    });
  },

  getPage: (pageId: string) => {
    return get().pages.get(pageId);
  },

  getPageByPath: (path: string) => {
    // First try exact match (e.g. /entities → /entities)
    const exactId = get().pathToPage.get(path);
    if (exactId) return get().pages.get(exactId);

    // Fall back to pattern matching for wildcard and parameterised routes.
    // e.g. registered path "/user-profile/ui/*" should match "/user-profile/ui/dashboard"
    // and "/entities/:entityId" should match "/entities/abc123".
    for (const page of get().pages.values()) {
      const pattern = page.path;

      // Wildcard route support: "/foo/*" should match both "/foo" and "/foo/..."
      if (pattern.endsWith('/*')) {
        const basePath = pattern.slice(0, -2);
        if (path === basePath || path.startsWith(`${basePath}/`)) {
          return page;
        }
      }

      // Skip non-parameterised routes (already checked via exact/wildcard match)
      if (!pattern.includes(':')) continue;

      // Convert route pattern to regex:
      //   "/entities/:entityId" → /^\/entities\/[^/]+\/?$/
      const regexStr = '^' + pattern.replace(/:[^/]+/g, '[^/]+') + '/?$';
      if (new RegExp(regexStr).test(path)) {
        return page;
      }
    }

    return undefined;
  },

  getAllPages: () => {
    return Array.from(get().pages.values());
  },

  getNavigablePages: () => {
    return Array.from(get().pages.values())
      .filter(p => p.showInNav)
      .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  },

  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },

  setError: (error: string | null) => {
    set({ error });
  },

  syncFromBackend: (pages: PluginPageDefinition[]) => {
    const newPages = new Map<string, PluginPageDefinition>();
    const newPathToPage = new Map<string, string>();
    
    for (const page of pages) {
      newPages.set(page.id, page);
      newPathToPage.set(page.path, page.id);
    }
    
    set({ pages: newPages, pathToPage: newPathToPage, isLoading: false });
  },
}));

/**
 * Generate React Router routes from registered pages
 */
export function generatePluginRoutes(): Array<{
  path: string;
  element: React.ReactNode;
}> {
  const pages = usePageRegistry.getState().getAllPages();
  
  // This will be used by PluginPageHost to render the appropriate component
  return pages.map(page => ({
    path: page.path,
    element: null, // Will be handled by PluginPageHost
  }));
}
